/**
 * Student & Academic Ops — Student List (ClarosCoreStudents.jsx)
 *
 * Architecture (2026-09 canonical slice):
 *   PRIMARY:  GET /api/core/students  →  PostgreSQL (CANONICAL_PG)
 *   SECONDARY: GET /api/v1/core/students  →  MongoDB (TRANSITIONAL — legacy operational data)
 *
 * The canonical PG list is the primary list. MongoDB data is secondary and
 * explicitly labelled as transitional. Users see the target architecture.
 *
 * Source tracking: development builds can show data-source badges; production
 * should not expose database implementation details to end users.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout/Shell";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { useInstitution } from "../context/InstitutionContext";
import { api } from "../lib/api";
import { Users, Loader2, Filter, ChevronRight, AlertTriangle, Database } from "lucide-react";
import { LegacyDataBanner } from "../components/shared/LegacyDataBanner";

const PG_STATUSES = ["ALL", "ACTIVE", "GRADUATED", "APPLICANT", "ENROLLED", "SUSPENDED", "LEAVE", "WITHDRAWN", "ALUMNUS"];
const MONGO_STATUSES = ["ALL", "ACTIVE", "GRADUATED", "DROPPED", "SUSPENDED"];

const IS_DEV = process.env.NODE_ENV !== "production";

function SourceBadge({ source }) {
  if (!IS_DEV) return null;
  return source === "CANONICAL_PG"
    ? <Badge variant="outline" className="text-[9px] text-emerald-700 border-emerald-300 bg-emerald-50">CANONICAL PG</Badge>
    : <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300 bg-amber-50">TRANSITIONAL</Badge>;
}

function statusBadge(s) {
  const map = {
    ACTIVE: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
    GRADUATED: "bg-blue-500/15 text-blue-700 border-blue-500/30",
    ENROLLED: "bg-sky-500/15 text-sky-700 border-sky-500/30",
    LEAVE: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    SUSPENDED: "bg-red-500/15 text-red-700 border-red-500/30",
    WITHDRAWN: "bg-gray-500/15 text-gray-600 border-gray-500/30",
    ALUMNUS: "bg-purple-500/15 text-purple-700 border-purple-500/30",
  };
  const cls = map[s] || "bg-gray-100 text-gray-600";
  return <Badge variant="outline" className={`text-[10px] ${cls}`}>{s}</Badge>;
}

// ---------------------------------------------------------------------------
// Canonical (PG) student list
// ---------------------------------------------------------------------------
function CanonicalStudentList({ students, loading, error, total }) {
  if (loading) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground" data-testid="canonical-students-loading">
        <Loader2 className="h-4 w-4 mx-auto animate-spin mb-2" /> Loading canonical students…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground" data-testid="canonical-students-error">
        <AlertTriangle className="h-4 w-4 mx-auto mb-2 opacity-60 text-amber-500" />
        <div className="font-medium text-amber-700">Could not load canonical students</div>
        <div className="text-xs mt-1 opacity-70">{error}</div>
      </div>
    );
  }
  if (!students || students.length === 0) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground" data-testid="canonical-students-empty">
        <Database className="h-4 w-4 mx-auto mb-2 opacity-50" />
        No canonical students found for current filters.
      </div>
    );
  }
  return (
    <Table data-testid="canonical-students-table">
      <TableHeader>
        <TableRow>
          <TableHead>Student No.</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="hidden md:table-cell">Admitted</TableHead>
          {IS_DEV && <TableHead className="text-right">Source</TableHead>}
          <TableHead className="w-8"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {students.map((s) => (
          <TableRow key={s.id} className="hover:bg-muted/30" data-testid={`canonical-student-row-${s.student_number}`}>
            <TableCell className="font-mono text-xs">{s.student_number}</TableCell>
            <TableCell className="font-medium text-sm">
              {s.display_name || `${s.given_name || ""} ${s.family_name || ""}`.trim() || "—"}
            </TableCell>
            <TableCell>{statusBadge(s.lifecycle_status)}</TableCell>
            <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
              {s.admission_date || "—"}
            </TableCell>
            {IS_DEV && <TableCell className="text-right"><SourceBadge source="CANONICAL_PG" /></TableCell>}
            <TableCell>
              <Link to={`/core/students/${s.id}`} data-testid={`canonical-student-link-${s.student_number}`}>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function ClarosCoreStudents() {
  const { current } = useInstitution();

  // --- Canonical PG state ---
  const [pgStudents, setPgStudents] = useState([]);
  const [pgTotal, setPgTotal] = useState(0);
  const [pgLoading, setPgLoading] = useState(false);
  const [pgError, setPgError] = useState(null);
  const [pgStatus, setPgStatus] = useState("ALL");
  const [pgSearch, setPgSearch] = useState("");

  // --- Legacy Mongo state (transitional) ---
  const [mongoExpanded, setMongoExpanded] = useState(false);
  const [mongoRows, setMongoRows] = useState([]);
  const [mongoTotal, setMongoTotal] = useState(0);
  const [mongoLoading, setMongoLoading] = useState(false);

  // --- Canonical PG load ---
  const loadCanonical = useCallback(async () => {
    if (!current) return;
    setPgLoading(true);
    setPgError(null);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (pgStatus !== "ALL") params.set("lifecycle_status", pgStatus);
      if (pgSearch.trim()) params.set("search", pgSearch.trim());
      const { data } = await api.get(`/core/students?${params.toString()}`);
      setPgStudents(data.items || []);
      setPgTotal(data.total || 0);
    } catch (e) {
      setPgError(e?.response?.data?.detail || "Canonical student API unavailable");
      setPgStudents([]);
    } finally {
      setPgLoading(false);
    }
  }, [current, pgStatus, pgSearch]);

  // --- Legacy Mongo load (on-demand) ---
  const loadLegacy = useCallback(async () => {
    if (!current || mongoRows.length > 0) return;
    setMongoLoading(true);
    try {
      const { data } = await api.get(`/v1/core/students?page_size=200`);
      setMongoRows(data.items || []);
      setMongoTotal(data.total || 0);
    } catch {
      setMongoRows([]);
    } finally {
      setMongoLoading(false);
    }
  }, [current, mongoRows.length]);

  useEffect(() => { loadCanonical(); }, [loadCanonical]);

  useEffect(() => {
    if (mongoExpanded) loadLegacy();
  }, [mongoExpanded, loadLegacy]);

  if (!current) return null;

  return (
    <div data-testid="claros-core-students-page">
      <LegacyDataBanner module="Student Directory" canonicalRoute="/admissions/applications" canonicalLabel="View canonical applications" />
      <PageHeader
        eyebrow="Student & Academic Ops"
        title="Students"
        description="Canonical Student Core — PostgreSQL-backed identity and lifecycle."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1.5" data-testid="core-students-count">
              <Users className="h-3 w-3" /> {pgTotal} canonical
            </Badge>
            {IS_DEV && <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300">CANONICAL PG</Badge>}
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-5">
        {/* ---- Canonical filters ---- */}
        <div className="rounded-lg border border-border bg-card p-4 flex flex-wrap gap-3" data-testid="core-students-filters">
          <Input
            value={pgSearch}
            placeholder="Search by student number…"
            onChange={(e) => setPgSearch(e.target.value)}
            className="max-w-xs"
            data-testid="core-students-search-input"
          />
          <Select value={pgStatus} onValueChange={setPgStatus}>
            <SelectTrigger className="w-40" data-testid="core-students-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {PG_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* ---- Primary: Canonical PG list ---- */}
        <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="canonical-students-section">
          <CanonicalStudentList
            students={pgStudents}
            loading={pgLoading}
            error={pgError}
            total={pgTotal}
          />
        </div>

        {/* ---- Secondary: Transitional legacy notice ---- */}
        <div
          className="rounded-lg border border-dashed border-amber-300 bg-amber-50/50 dark:bg-amber-950/10"
          data-testid="legacy-students-section"
        >
          <button
            className="w-full flex items-center justify-between p-4 text-left group"
            onClick={() => setMongoExpanded(v => !v)}
            data-testid="legacy-students-toggle"
          >
            <div>
              <div className="text-xs font-semibold text-amber-800 dark:text-amber-400 flex items-center gap-1.5">
                <Database className="h-3.5 w-3.5" /> Legacy data available
                <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700 bg-transparent ml-1">TRANSITIONAL</Badge>
              </div>
              <div className="text-xs text-amber-700/70 dark:text-amber-500/60 mt-0.5">
                {mongoTotal > 0 ? `${mongoTotal} records` : "Historical student data"} remaining on the transitional platform.
                Not canonical. No new writes are made here.
              </div>
            </div>
            <ChevronRight className={`h-4 w-4 text-amber-600 transition-transform ${mongoExpanded ? "rotate-90" : ""}`} />
          </button>

          {mongoExpanded && (
            <div className="border-t border-amber-200">
              {mongoLoading ? (
                <div className="p-6 text-center text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin mx-auto mb-1" /> Loading legacy records…
                </div>
              ) : mongoRows.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">No legacy records found.</div>
              ) : (
                <Table data-testid="legacy-students-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Roll No.</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      {IS_DEV && <TableHead className="text-right">Source</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mongoRows.slice(0, 25).map((s) => (
                      <TableRow key={s.id} className="opacity-70" data-testid={`legacy-student-row-${s.roll_number}`}>
                        <TableCell className="font-mono text-xs">{s.roll_number}</TableCell>
                        <TableCell className="text-sm">{s.display_name || "—"}</TableCell>
                        <TableCell>{statusBadge(s.status)}</TableCell>
                        {IS_DEV && <TableCell className="text-right"><SourceBadge source="TRANSITIONAL" /></TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
